import { db, auth, functions } from '../firebase';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  Timestamp,
  addDoc,
  runTransaction,
  deleteField,
  onSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Wallet, Transaction } from '../types';
import { API_BASE } from '../utils/apiBase';

/**
 * Get auth token for API calls
 */
async function getAuthToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

/**
 * Make authenticated API call
 */
async function apiCall<T>(endpoint: string, method: 'GET' | 'POST' = 'POST', body?: object): Promise<T> {
  const token = await getAuthToken();
  
  const options: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  };
  
  if (body && method === 'POST') {
    options.body = JSON.stringify(body);
  }
  
  const response = await fetch(`${API_BASE}${endpoint}`, options);
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.error || `API call failed: ${response.status}`);
  }
  
  return data;
}

class WalletService {
  // ============================================================================
  // WALLET CRUD
  // ============================================================================

  /**
   * Get user wallet
   */
  async getWallet(userId: string): Promise<Wallet | null> {
    try {
      const walletDoc = await getDoc(doc(db, 'wallets', userId));
      return walletDoc.exists() ? (walletDoc.data() as Wallet) : null;
    } catch (error) {
      console.error('Error getting wallet:', error);
      return null;
    }
  }

  /**
   *  NEW: Get wallet with chips at tables (for display)

   * Uses server API for accurate data
   */
  async getWalletWithTableChips(): Promise<{
    playCoins: number;
    pkCash: number;
    tableChips: { [tableId: string]: { chips: number; lastSync: number } };
    totalPlayCoins: number;
    totalPkCash: number;
  }> {
    try {
      return await apiCall('/getWalletWithTableChips', 'GET');
    } catch (error) {
      console.error('Error getting wallet with table chips:', error);
      // Fallback to basic wallet
      const wallet = await this.getWallet(auth.currentUser?.uid || '');
      return {
        playCoins: wallet?.playCoins || 0,
        pkCash: wallet?.pkCash || 0,
        tableChips: {},
        totalPlayCoins: wallet?.playCoins || 0,
        totalPkCash: wallet?.pkCash || 0,
      };
    }
  }

  /**
   *  NEW: Sync current table chips to wallet (crash protection)

   * Call this on reconnect or periodically
   */
  async syncTableChips(tableId: string): Promise<{ success: boolean; chips: number }> {
    try {
      console.log(`💰 [WALLET] Syncing chips for table ${tableId}`);
      return await apiCall('/syncTableChips', 'POST', { tableId });
    } catch (error) {
      console.error('Error syncing table chips:', error);
      return { success: false, chips: 0 };
    }
  }

  /**
   *  NEW: Subscribe to wallet changes (real-time updates)

   */
  subscribeToWallet(userId: string, callback: (wallet: Wallet | null) => void): () => void {
    const walletRef = doc(db, 'wallets', userId);
    
    const unsubscribe = onSnapshot(walletRef, (snapshot) => {
      if (snapshot.exists()) {
        callback(snapshot.data() as Wallet);
      } else {
        callback(null);
      }
    }, (error) => {
      console.error('Wallet subscription error:', error);
      callback(null);
    });
    
    return unsubscribe;
  }

  /**
   * Create wallet for new user (signup)
   * Initial: 1000 PlayCoins + $0 PKCash
   */
  async createWallet(userId: string): Promise<Wallet> {
    //  FIX: Check if wallet already exists (idempotent)

    try {
      const existingWallet = await this.getWallet(userId);
      if (existingWallet) {
        console.log(`⚠️ [WalletService] Wallet already exists for ${userId.substring(0, 8)}, returning existing`);
        return existingWallet;
      }
    } catch (e) {
      // Wallet doesn't exist, continue creating
    }

    const now = new Date();
    const today = now.toISOString().split('T')[0];
    
    const wallet: Wallet = {
      userId,
      playCoins: 1000,
      pkCash: 0,
      //  PLAYTHROUGH TRACKING

      playthroughRequired: 0,
      playthroughCompleted: 0,
      lastDailyBonusDate: today,
      totalPlayCoinsWon: 0,
      totalPlayCoinsLost: 0,
      totalPKCashWon: 0,
      totalPKCashLost: 0,
      gamesPlayed: 0,
      updatedAt: now,
    };

    try {
      await setDoc(doc(db, 'wallets', userId), wallet);
      
      await this.logTransaction({
        userId,
        type: 'daily_bonus',
        currency: 'playcoins',
        amount: 1000,
        description: 'Welcome bonus: 1000 PlayCoins',
        balanceSnapshot: 1000,
      });
      
      return wallet;
    } catch (error) {
      console.error('Error creating wallet:', error);
      throw error;
    }
  }

  // ============================================================================
  // PLAYTHROUGH TRACKING
  // ============================================================================

  /**
   * Get playthrough status for a user
   */
  async getPlaythroughStatus(userId: string): Promise<{
    playthroughRequired: number;
    playthroughCompleted: number;
    playthroughRemaining: number;
    isPlaythroughMet: boolean;
    withdrawableAmount: number;
    totalPKCash: number;
  }> {
    try {
      const wallet = await this.getWallet(userId);
      if (!wallet) {
        return {
          playthroughRequired: 0,
          playthroughCompleted: 0,
          playthroughRemaining: 0,
          isPlaythroughMet: true,
          withdrawableAmount: 0,
          totalPKCash: 0,
        };
      }

      const playthroughRequired = wallet.playthroughRequired || 0;
      const playthroughCompleted = wallet.playthroughCompleted || 0;
      const playthroughRemaining = Math.max(0, playthroughRequired - playthroughCompleted);
      const isPlaythroughMet = playthroughCompleted >= playthroughRequired;
      const totalPKCash = wallet.pkCash || 0;

      let withdrawableAmount = 0;
      if (isPlaythroughMet) {
        withdrawableAmount = totalPKCash;
      } else if (playthroughRequired > 0) {
        const playthroughPercentage = Math.min(1, playthroughCompleted / playthroughRequired);
        withdrawableAmount = Math.floor(totalPKCash * playthroughPercentage * 100) / 100;
      }

      return {
        playthroughRequired,
        playthroughCompleted,
        playthroughRemaining,
        isPlaythroughMet,
        withdrawableAmount,
        totalPKCash,
      };
    } catch (error) {
      console.error('Error getting playthrough status:', error);
      return {
        playthroughRequired: 0,
        playthroughCompleted: 0,
        playthroughRemaining: 0,
        isPlaythroughMet: false,
        withdrawableAmount: 0,
        totalPKCash: 0,
      };
    }
  }

  /**
   * Add playthrough requirement (called on deposit/purchase)
   */
  async addPlaythroughRequirement(
    userId: string,
    depositAmount: number,
    multiplier: number = 1
  ): Promise<void> {
    try {
      const wallet = await this.getWallet(userId);
      if (!wallet) return;

      const additionalPlaythrough = depositAmount * multiplier;
      
      await updateDoc(doc(db, 'wallets', userId), {
        playthroughRequired: (wallet.playthroughRequired || 0) + additionalPlaythrough,
        updatedAt: new Date(),
      });

      console.log(`📊 Added playthrough requirement: $${additionalPlaythrough} for user ${userId.substring(0, 8)}`);
    } catch (error) {
      console.error('Error adding playthrough requirement:', error);
    }
  }

  /**
   * Credit referral signup bonus via Cloud Function API
   *  PRODUCTION: Server-side processing for security

   * 
   * This handles:
   * - $5 PKCash bonus to new user (with 1x playthrough)
   * - $5 PKCash bonus to referrer
   * - Agent chain tracking (rootAgentId propagation)
   * - Referral record creation
   * 
   * @param userId - The new user's ID
   * @returns Promise<boolean> - Success status
   */
  async creditReferralBonus(userId: string): Promise<boolean> {
    try {
      console.log(`🎁 [WalletService] Requesting referral bonus for: ${userId.substring(0, 8)}`);
      
      const result = await apiCall<{
        success: boolean;
        message?: string;
        alreadyCredited?: boolean;
        newUserBonus?: number;
        referrerBonus?: number;
        referrerId?: string;
        rootAgentId?: string;
      }>('/creditReferralBonus', 'POST', { userId });
      
      if (result.success) {
        if (result.alreadyCredited) {
          console.log(`   ℹ️ Bonus already credited`);
        } else {
          console.log(`   ✅ Referral bonus credited!`);
          if (result.newUserBonus) console.log(`      New user: +$${result.newUserBonus} PKCash`);
          if (result.referrerBonus) console.log(`      Referrer: +$${result.referrerBonus} PKCash`);
          if (result.rootAgentId) console.log(`      Root agent: ${result.rootAgentId.substring(0, 8)}`);
        }
        return true;
      } else {
        console.log(`   ⚠️ Bonus not credited: ${result.message}`);
        return false;
      }
      
    } catch (error) {
      console.error('❌ Error crediting referral bonus:', error);
      // Don't throw - return false so signup flow continues
      return false;
    }
  }

  /**
   * Record playthrough progress (called on PKCash buy-in/wager)
   */
  async recordPlaythroughProgress(userId: string, amountWagered: number): Promise<void> {
    try {
      const wallet = await this.getWallet(userId);
      if (!wallet) return;

      await updateDoc(doc(db, 'wallets', userId), {
        playthroughCompleted: (wallet.playthroughCompleted || 0) + amountWagered,
        updatedAt: new Date(),
      });

      console.log(`📊 Playthrough progress: +$${amountWagered} wagered for user ${userId.substring(0, 8)}`);
    } catch (error) {
      console.error('Error recording playthrough progress:', error);
    }
  }

  /**
   * Validate withdrawal against playthrough requirements
   */
  async validateWithdrawal(
    userId: string,
    requestedAmount: number
  ): Promise<{ valid: boolean; maxWithdrawable: number; message?: string }> {
    try {
      const status = await this.getPlaythroughStatus(userId);

      if (requestedAmount > status.totalPKCash) {
        return {
          valid: false,
          maxWithdrawable: status.withdrawableAmount,
          message: 'Insufficient PKCash balance',
        };
      }

      if (requestedAmount > status.withdrawableAmount) {
        return {
          valid: false,
          maxWithdrawable: status.withdrawableAmount,
          message: `Playthrough not complete. Wager $${status.playthroughRemaining.toFixed(2)} more to unlock full balance. Current withdrawable: $${status.withdrawableAmount.toFixed(2)}`,
        };
      }

      return { valid: true, maxWithdrawable: status.withdrawableAmount };
    } catch (error) {
      console.error('Error validating withdrawal:', error);
      return { valid: false, maxWithdrawable: 0, message: 'Validation error' };
    }
  }

  // ============================================================================
  // DAILY BONUS
  // ============================================================================

  /**
   * Check and claim daily login bonus (1000 PlayCoins)
   *  SECURE: Uses Cloud Function to prevent exploitation

   */
  async claimDailyBonus(userId: string): Promise<number> {
    try {
      console.log(`🎁 [DailyBonus] Claiming via Cloud Function for ${userId.substring(0, 8)}...`);
      
      const claimBonus = httpsCallable(functions, 'claimDailyBonus');
      
      const result = await claimBonus({});
      const data = result.data as {
        success: boolean;
        amount: number;
        newBalance?: number;
        message?: string;
        alreadyClaimed?: boolean;
      };
      
      if (data.success) {
        console.log(`✅ [DailyBonus] Claimed ${data.amount} PlayCoins for ${userId.substring(0, 8)}`);
        return data.amount;
      } else {
        console.log(`ℹ️ [DailyBonus] ${data.message || 'Not claimed'}`);
        return 0;
      }
    } catch (error) {
      console.error('Error claiming daily bonus:', error);
      return 0;
    }
  }

  /**
   * Check if daily bonus is available
   */
  async isDailyBonusAvailable(userId: string): Promise<boolean> {
    try {
      const wallet = await this.getWallet(userId);
      if (!wallet) return true; // New user can claim

      const today = new Date().toISOString().split('T')[0];
      return wallet.lastDailyBonusDate !== today;
    } catch (error) {
      console.error('Error checking daily bonus:', error);
      return false;
    }
  }

  // ============================================================================
  // TABLE CHIP OPERATIONS
  // ============================================================================

  /**
   *  DEPRECATED: Use server API secureJoinTable instead

   * This is kept for backward compatibility but should not be used
   */
  async buyInToTable(
    userId: string,
    amount: number,
    currency: 'playcoins' | 'pkcash',
    tableId: string
  ): Promise<{ success: boolean; error?: string }> {
    console.warn('⚠️ buyInToTable is deprecated. Use gameApiService.secureJoinTable instead.');
    // This should not be called anymore - server handles buy-ins
    return { success: false, error: 'Use server API for buy-ins' };
  }

  /**
   *  DEPRECATED: Use server API secureLeaveTable instead

   * Chips are returned via server transaction
   */
  async cashOutFromTable(
    userId: string,
    amount: number,
    currency: 'playcoins' | 'pkcash',
    tableId: string
  ): Promise<{ success: boolean; error?: string }> {
    console.warn('⚠️ cashOutFromTable is deprecated. Use gameApiService.secureLeaveTable instead.');
    // This should not be called anymore - server handles cash-outs
    return { success: false, error: 'Use server API for cash-outs' };
  }

  // ============================================================================
  // GAME WIN/LOSS TRACKING (Statistics Only)
  // ============================================================================
  // Note: Actual chip movements are handled by the server
  // These methods only update statistics in the wallet

  /**
   * Track game win (statistics only - chips moved by server)
   */
  async trackGameWin(
    userId: string,
    amount: number,
    currency: 'playcoins' | 'pkcash',
    tableId?: string,
    handNumber?: number
  ): Promise<void> {
    try {
      const wallet = await this.getWallet(userId);
      if (!wallet) return;

      const update: any = {
        gamesPlayed: (wallet.gamesPlayed || 0) + 1,
        updatedAt: new Date(),
      };

      if (currency === 'playcoins') {
        update.totalPlayCoinsWon = (wallet.totalPlayCoinsWon || 0) + amount;
      } else {
        update.totalPKCashWon = (wallet.totalPKCashWon || 0) + amount;
      }

      await updateDoc(doc(db, 'wallets', userId), update);
      console.log(`📊 Tracked win: ${amount} ${currency} for ${userId.substring(0, 8)}`);
    } catch (error) {
      console.error('Error tracking game win:', error);
    }
  }

  /**
   * Track game loss (statistics only - chips moved by server)
   */
  async trackGameLoss(
    userId: string,
    amount: number,
    currency: 'playcoins' | 'pkcash',
    tableId?: string,
    handNumber?: number
  ): Promise<void> {
    try {
      const wallet = await this.getWallet(userId);
      if (!wallet) return;

      const update: any = {
        gamesPlayed: (wallet.gamesPlayed || 0) + 1,
        updatedAt: new Date(),
      };

      if (currency === 'playcoins') {
        update.totalPlayCoinsLost = (wallet.totalPlayCoinsLost || 0) + amount;
      } else {
        update.totalPKCashLost = (wallet.totalPKCashLost || 0) + amount;
      }

      await updateDoc(doc(db, 'wallets', userId), update);
      console.log(`📊 Tracked loss: ${amount} ${currency} for ${userId.substring(0, 8)}`);
    } catch (error) {
      console.error('Error tracking game loss:', error);
    }
  }

  /**
   * recordGameWin - LEGACY alias for trackGameWin
   */
  async recordGameWin(
    userId: string,
    amount: number,
    currency: 'playcoins' | 'pkcash',
    tableId?: string,
    handNumber?: number
  ): Promise<Wallet | null> {
    await this.trackGameWin(userId, amount, currency, tableId, handNumber);
    return this.getWallet(userId);
  }

  /**
   * recordGameLoss - LEGACY alias for trackGameLoss
   */
  async recordGameLoss(
    userId: string,
    amount: number,
    currency: 'playcoins' | 'pkcash',
    tableId?: string,
    handNumber?: number
  ): Promise<Wallet | null> {
    await this.trackGameLoss(userId, amount, currency, tableId, handNumber);
    return this.getWallet(userId);
  }

  // ============================================================================
  // BALANCE QUERIES
  // ============================================================================

  /**
   * Get balance for specific currency
   */
  async getBalance(userId: string, currency: 'playcoins' | 'pkcash'): Promise<number> {
    const wallet = await this.getWallet(userId);
    if (!wallet) return 0;
    return currency === 'playcoins' ? wallet.playCoins : wallet.pkCash;
  }

  /**
   * Get both balances with stats
   */
  async getBothBalances(userId: string) {
    const wallet = await this.getWallet(userId);
    return {
      playCoins: wallet?.playCoins || 0,
      pkCash: wallet?.pkCash || 0,
      pkCashPlaythrough: wallet?.pkCashPlaythrough || 0, //  FIX: Include playthrough requirement

      pkCashHeld: wallet?.pkCashHeld || 0, //  FIX: Include held amount for pending withdrawals

      playthroughRequired: wallet?.playthroughRequired || 0,
      playthroughCompleted: wallet?.playthroughCompleted || 0,
      totalPlayCoinsWon: wallet?.totalPlayCoinsWon || 0,
      totalPlayCoinsLost: wallet?.totalPlayCoinsLost || 0,
      totalPKCashWon: wallet?.totalPKCashWon || 0,
      totalPKCashLost: wallet?.totalPKCashLost || 0,
      gamesPlayed: wallet?.gamesPlayed || 0,
      lastDailyBonusDate: wallet?.lastDailyBonusDate || null,
      rakeBalance: wallet?.rakeBalance || 0, //  ENTERPRISE: Separate agent commission balance

    };
  }

  /**
   *  NEW: Get total balance including chips at tables

   */
  async getTotalBalance(userId: string): Promise<{
    wallet: number;
    tables: number;
    total: number;
  }> {
    try {
      const data = await this.getWalletWithTableChips();
      const tableTotal = Object.values(data.tableChips || {}).reduce(
        (sum: number, tc: any) => sum + (tc.chips || 0), 
        0
      );
      return {
        wallet: data.playCoins,
        tables: tableTotal,
        total: data.playCoins + tableTotal,
      };
    } catch (error) {
      console.error('Error getting total balance:', error);
      const wallet = await this.getWallet(userId);
      return {
        wallet: wallet?.playCoins || 0,
        tables: 0,
        total: wallet?.playCoins || 0,
      };
    }
  }

  // ============================================================================
  // PURCHASES
  // ============================================================================

  /**
   * Purchase PlayCoins (sweepstakes model)
   * $1 = 1000 PlayCoins + $1 PKCash bonus
   */
  async purchaseCoins(userId: string, usdAmount: number, stripePaymentId: string): Promise<Wallet> {
    const wallet = await this.getWallet(userId);
    if (!wallet) throw new Error('Wallet not found');

    const playcoinAmount = usdAmount * 1000;
    const pkcashBonus = usdAmount;
    const playthroughRequired = usdAmount;

    const updatedWallet: Wallet = {
      ...wallet,
      playCoins: wallet.playCoins + playcoinAmount,
      pkCash: wallet.pkCash + pkcashBonus,
      playthroughRequired: (wallet.playthroughRequired || 0) + playthroughRequired,
      updatedAt: new Date(),
    };

    await updateDoc(doc(db, 'wallets', userId), updatedWallet);

    await this.logTransaction({
      userId,
      type: 'purchase',
      currency: 'playcoins',
      amount: playcoinAmount,
      description: `Purchase: ${playcoinAmount} PlayCoins`,
      balanceSnapshot: updatedWallet.playCoins,
    });

    await this.logTransaction({
      userId,
      type: 'purchase',
      currency: 'pkcash',
      amount: pkcashBonus,
      description: `Sweepstakes Prize: $${pkcashBonus.toFixed(2)} PKCash (1x playthrough required)`,
      balanceSnapshot: updatedWallet.pkCash,
    });

    console.log(`📊 Added playthrough requirement: $${playthroughRequired} for purchase`);

    return updatedWallet;
  }

  // ============================================================================
  //  GAME ITEMS PURCHASES

  // ============================================================================

  /**
   * Purchase game item - Deducts PKCash for in-game items
   *  PRODUCTION: Uses Cloud Function for secure atomic transaction

   */
  async purchaseGameItem(
    userId: string,
    amount: number,
    itemName: string,
    tableId: string,
    targetPlayerId?: string
  ): Promise<{ success: boolean; newBalance: number; error?: string }> {
    try {
      //  PRODUCTION: Call secure Cloud Function instead of direct Firestore write

      const result = await apiCall<{ success: boolean; newBalance: number; error?: string }>(
        '/purchaseGameItem',
        'POST',
        { amount, itemName, tableId, targetPlayerId }
      );

      if (result.success) {
        console.log(`🎁 Game item purchased: ${itemName} for $${amount.toFixed(2)} by ${userId.substring(0, 8)}`);
      }

      return result;
    } catch (error) {
      console.error('❌ Error purchasing game item:', error);
      return { success: false, newBalance: 0, error: 'Purchase failed' };
    }
  }

  /**
   * Check if user can afford a game item
   */
  async canAffordGameItem(userId: string, amount: number): Promise<boolean> {
    const wallet = await this.getWallet(userId);
    if (!wallet) return false;
    return wallet.pkCash >= amount;
  }

  // ============================================================================
  // TRANSACTION LOGGING
  // ============================================================================

  /**
   * Log transaction for audit trail
   */
  async logTransaction(
    transaction: Omit<Transaction, 'id' | 'timestamp'> & { tableId?: string; handNumber?: number }
  ): Promise<string> {
    try {
      if (!transaction.userId) {
        throw new Error('Transaction must include userId');
      }

      const transactionData: Record<string, any> = {
        userId: transaction.userId,
        type: transaction.type,
        currency: transaction.currency,
        amount: transaction.amount,
        description: transaction.description,
        balanceSnapshot: transaction.balanceSnapshot,
        timestamp: Timestamp.now(),
      };

      if (transaction.tableId) {
        transactionData.tableId = transaction.tableId;
      }
      if (transaction.handNumber !== undefined) {
        transactionData.handNumber = transaction.handNumber;
      }

      const docRef = await addDoc(
        collection(db, 'transactions'),
        transactionData
      );

      return docRef.id;
    } catch (error) {
      console.error('❌ Error logging transaction:', error);
      throw error;
    }
  }

  /**
   * Get transaction history
   */
  async getTransactionHistory(userId: string, limitCount: number = 20): Promise<Transaction[]> {
    try {
      const q = query(
        collection(db, 'transactions'),
        where('userId', '==', userId)
      );

      const querySnapshot = await getDocs(q);
      const transactions: Transaction[] = [];

      querySnapshot.forEach((doc) => {
        const d: any = doc.data();
        // Transactions come from TWO writers with different field names:
        // the client (logTransaction) stamps `timestamp`, Cloud Functions
        // stamp `createdAt`. Reading only `timestamp` left every
        // server-written row - daily bonus, wheel spin, tournament prizes,
        // admin credits - with an undefined date, which sorted them to the
        // bottom and pushed them off the end of the slice below. They were
        // paid, just invisible. Accept either field.
        const raw = d.timestamp ?? d.createdAt;
        const ts = raw?.toDate ? raw.toDate() : (raw ? new Date(raw) : undefined);
        transactions.push({
          id: doc.id,
          ...d,
          timestamp: ts,
        } as Transaction);
      });

      return transactions
        .sort((a, b) => {
          const timeA = a.timestamp?.getTime() || 0;
          const timeB = b.timestamp?.getTime() || 0;
          return timeB - timeA;
        })
        .slice(0, limitCount);
    } catch (error) {
      console.error('Error getting transaction history:', error);
      return [];
    }
  }
}

export default new WalletService();